import { memo, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { getInputClasses, labelClasses } from '../../styles/inputStyles'

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December']

function fmt12(timeStr) {
  if (!timeStr) return ''
  const [h, m] = timeStr.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`
}

function toDateStr(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/**
 * BookingStepSchedule - Step 2: availability calendar + time selection.
 *
 * Shows a month calendar where:
 *  - Days in the vendor's recurring schedule are highlighted (available)
 *  - Past days are disabled
 *  - The user picks a day, then picks a time slot from the available windows
 *  - Fallback to manual date/time picker if vendor has no schedule set
 *
 * @idempotent @atomic
 */
const BookingStepSchedule = memo(function BookingStepSchedule({
  schedulingType,
  onTypeChange,
  scheduledDate,
  onDateChange,
  scheduledTime,
  onTimeChange,
  contactHint,
  onContactHintChange,
  timePrefs,
  onAddTimePref,
  onRemoveTimePref,
  onUpdateTimePref,
  vendorSchedule = [],    // array of { dayOfWeek, startTime, endTime }
  vendorEvents = [],      // array of { eventDate, startTime, endTime, title }
  selectedOffering = null,
}) {
  const { t } = useTranslation()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const [calYear, setCalYear] = useState(today.getFullYear())
  const [calMonth, setCalMonth] = useState(today.getMonth())

  // Available days of week from recurring schedule (Set of 0-6)
  const availableDow = useMemo(() => new Set(vendorSchedule.map(s => s.dayOfWeek)), [vendorSchedule])

  // One-off event dates as a map: dateStr → event
  const eventMap = useMemo(() => {
    const m = {}
    vendorEvents.forEach(ev => { if (ev.eventDate) m[ev.eventDate] = ev })
    return m
  }, [vendorEvents])

  const hasSchedule = vendorSchedule.length > 0 || vendorEvents.length > 0

  // Calendar grid
  const calDays = useMemo(() => {
    const firstDay = new Date(calYear, calMonth, 1).getDay()
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate()
    const cells = []
    for (let i = 0; i < firstDay; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(d)
    return cells
  }, [calYear, calMonth])

  const isDayAvailable = useCallback((d) => {
    const date = new Date(calYear, calMonth, d)
    if (date < today) return false
    const dow = date.getDay()
    const dateStr = toDateStr(calYear, calMonth, d)
    return availableDow.has(dow) || !!eventMap[dateStr]
  }, [calYear, calMonth, today, availableDow, eventMap])

  const handleDayClick = useCallback((d) => {
    if (!isDayAvailable(d)) return
    const dateStr = toDateStr(calYear, calMonth, d)
    onDateChange(dateStr)
    // Pre-fill time from recurring slot for this day's DOW
    const dow = new Date(calYear, calMonth, d).getDay()
    const slot = vendorSchedule.find(s => s.dayOfWeek === dow)
    const event = eventMap[dateStr]
    const startTime = event?.startTime || slot?.startTime || ''
    if (startTime && !scheduledTime) onTimeChange(startTime)
  }, [isDayAvailable, calYear, calMonth, onDateChange, vendorSchedule, eventMap, scheduledTime, onTimeChange])

  // Time slots for selected date
  const timeSlotsForDate = useMemo(() => {
    if (!scheduledDate) return []
    const dow = new Date(scheduledDate + 'T00:00:00').getDay()
    const slots = vendorSchedule.filter(s => s.dayOfWeek === dow)
    const event = eventMap[scheduledDate]
    if (event) return [{ start: event.startTime, end: event.endTime, label: event.title }]
    return slots.map(s => ({ start: s.startTime, end: s.endTime, label: `${fmt12(s.startTime)} – ${fmt12(s.endTime)}` }))
  }, [scheduledDate, vendorSchedule, eventMap])

  const prevMonth = useCallback(() => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1) }
    else setCalMonth(m => m - 1)
  }, [calMonth])

  const nextMonth = useCallback(() => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1) }
    else setCalMonth(m => m + 1)
  }, [calMonth])

  // Duration chip
  const durationChip = selectedOffering?.durationMinutes
    ? <span className="ml-1.5 text-[11px] text-gray-400 font-normal">· {selectedOffering.durationMinutes} min</span>
    : null

  return (
    <div className="p-3 space-y-3">
      {/* ── Availability calendar ──────────────────────────────── */}
      {hasSchedule ? (
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-1.5">
            {t('bookings.pickDate', 'Pick a date')}
            {durationChip}
          </p>

          {/* Month nav */}
          <div className="flex items-center justify-between mb-1.5">
            <button type="button" onClick={prevMonth} onTouchEnd={(e) => { e.preventDefault(); prevMonth() }}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-gray-800">
              {MONTH_NAMES[calMonth]} {calYear}
            </span>
            <button type="button" onClick={nextMonth} onTouchEnd={(e) => { e.preventDefault(); nextMonth() }}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500">
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-0.5">
            {DAY_LABELS.map(d => (
              <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-0.5">{d}</div>
            ))}
          </div>

          {/* Calendar grid — compact cells */}
          <div className="grid grid-cols-7 gap-0.5">
            {calDays.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} />
              const dateStr = toDateStr(calYear, calMonth, d)
              const isAvailable = isDayAvailable(d)
              const isSelected = scheduledDate === dateStr
              const isToday = dateStr === toDateStr(today.getFullYear(), today.getMonth(), today.getDate())
              const hasEvent = !!eventMap[dateStr]

              return (
                <button
                  key={d}
                  type="button"
                  disabled={!isAvailable}
                  onClick={() => handleDayClick(d)}
                  onTouchEnd={(e) => { e.preventDefault(); handleDayClick(d) }}
                  className={`relative h-7 flex items-center justify-center text-[11px] rounded transition-all font-medium
                    ${isSelected ? 'bg-primary text-white shadow-sm' : ''}
                    ${!isSelected && isAvailable ? 'bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer' : ''}
                    ${!isAvailable ? 'text-gray-300 cursor-not-allowed' : ''}
                    ${isToday && !isSelected ? 'ring-1 ring-primary' : ''}
                  `}
                >
                  {d}
                  {hasEvent && !isSelected && <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-accent" />}
                </button>
              )
            })}
          </div>

          {/* Time slot picker for selected date */}
          {scheduledDate && timeSlotsForDate.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                {t('bookings.pickTime', 'Pick a time')}
              </p>
              <div className="flex flex-wrap gap-2">
                {timeSlotsForDate.map((slot, i) => (
                  <button key={i} type="button"
                    onClick={() => onTimeChange(slot.start)}
                    onTouchEnd={(e) => { e.preventDefault(); onTimeChange(slot.start) }}
                    className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-all ${
                      scheduledTime === slot.start
                        ? 'bg-primary text-white border-primary'
                        : 'border-gray-200 text-gray-600 hover:border-primary hover:text-primary'
                    }`}>
                    {slot.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom time — only shown when no pre-set slots exist for this date */}
          {scheduledDate && timeSlotsForDate.length === 0 && (
            <div className="mt-2">
              <label className={labelClasses + ' flex items-center gap-1'}>
                {t('bookings.time', 'Time')}
                <span className="text-gray-400 font-normal">{t('common.optional', '(optional)')}</span>
              </label>
              <input type="time" value={scheduledTime} onChange={(e) => onTimeChange(e.target.value)}
                className={getInputClasses(false)} />
            </div>
          )}
        </div>
      ) : (
        /* No schedule set — fall back to manual pickers */
        <div>
          <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-2.5">
            <InformationCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 leading-snug">
              {t('bookings.noScheduleHint', "This provider hasn't set their schedule yet. Pick your preferred date and time and they'll confirm availability.")}
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <label className={labelClasses}>{t('bookings.date', 'Preferred date')}</label>
              <input type="date" value={scheduledDate} min={toDateStr(today.getFullYear(), today.getMonth(), today.getDate())}
                onChange={(e) => onDateChange(e.target.value)} className={getInputClasses(false)} />
            </div>
            <div>
              <label className={labelClasses}>{t('bookings.time', 'Preferred time')}</label>
              <input type="time" value={scheduledTime} onChange={(e) => onTimeChange(e.target.value)} className={getInputClasses(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Flexible mode */}
      <div>
        <button type="button"
          onClick={() => onTypeChange(schedulingType === 'flexible' ? 'single' : 'flexible')}
          onTouchEnd={(e) => { e.preventDefault(); onTypeChange(schedulingType === 'flexible' ? 'single' : 'flexible') }}
          className="text-xs text-primary hover:underline font-medium">
          {schedulingType === 'flexible'
            ? t('bookings.switchToSingle', '← Back to single date')
            : t('bookings.switchToFlexible', 'Offer multiple dates instead')}
        </button>
      </div>

      {schedulingType === 'flexible' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1.5 leading-snug">
            {t('bookings.flexibleHint', 'Add up to 3 preferred times — the vendor will choose one.')}
          </p>
          {timePrefs.map((pref, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input type="date" value={pref.preferred_date}
                min={toDateStr(today.getFullYear(), today.getMonth(), today.getDate())}
                onChange={(e) => onUpdateTimePref(idx, 'preferred_date', e.target.value)}
                className="flex-1 rounded-lg border border-gray-300 text-sm px-3 py-2" />
              <input type="time" value={pref.preferred_time}
                onChange={(e) => onUpdateTimePref(idx, 'preferred_time', e.target.value)}
                className="w-28 rounded-lg border border-gray-300 text-sm px-3 py-2" />
              {idx > 0 && (
                <button type="button" onClick={() => onRemoveTimePref(idx)}
                  onTouchEnd={(e) => { e.preventDefault(); onRemoveTimePref(idx) }}
                  className="text-gray-400 hover:text-red-500">
                  <ChevronLeftIcon className="w-4 h-4 rotate-180" />
                </button>
              )}
            </div>
          ))}
          {timePrefs.length < 3 && (
            <button type="button" onClick={onAddTimePref} onTouchEnd={(e) => { e.preventDefault(); onAddTimePref() }}
              className="text-xs text-primary hover:underline font-medium">
              + {t('bookings.addTimePref', 'Add another preferred time')}
            </button>
          )}
        </div>
      )}

      <div>
        <label className={labelClasses}>
          {t('bookings.contactHint', 'Best way to reach you')}
          <span className="text-gray-400 font-normal ml-1">{t('common.optional', '(optional)')}</span>
        </label>
        <input type="text" value={contactHint} onChange={(e) => onContactHintChange(e.target.value)}
          placeholder={t('bookings.contactHintPlaceholder', 'Phone or email — the vendor will use this to confirm details')}
          className={getInputClasses(false)} />
      </div>
    </div>
  )
})

export default BookingStepSchedule
